// Async-fetcher wrapper over the canonical MultiCombobox primitive. The
// primitive handles popover positioning, ARIA, chip rendering, and keyboard
// navigation; this wrapper owns the debounce + abort-safe fetch lifecycle
// (which the static-option primitive doesn't try to model) and the
// selected-items rehydration that the audience picker passes through.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  MultiCombobox,
  Pill,
  type ComboboxOption,
  type Tone,
} from '@leanin/ui';

export interface TypeaheadItem {
  readonly id: string;
}

export interface TypeaheadMultiSelectProps<T extends TypeaheadItem> {
  readonly placeholder?: string;
  readonly fetcher: (q: string) => Promise<readonly T[]>;
  readonly selectedIds: readonly string[];
  readonly selectedItems: readonly T[];
  readonly onChange: (ids: readonly string[], items: readonly T[]) => void;
  readonly renderItem: (item: T) => ReactNode;
  readonly renderSelectedLabel: (item: T) => ReactNode;
  readonly pillTone?: Tone;
  /** Defaults to `MultiCombobox`'s own localized "no matches" copy. */
  readonly emptyState?: string;
  /** The search input's id; `Field.Control` sets it so the Field label names the input. */
  readonly id?: string;
  /** Set by `Field.Control` so the Field's description and error reach the input. */
  readonly 'aria-describedby'?: string;
  /** Set by `Field.Control` while the Field shows an error. */
  readonly 'aria-invalid'?: boolean;
  /** Set by `Field.Control` while the Field's `required` prop is true. */
  readonly 'aria-required'?: boolean;
}

const DEBOUNCE_MS = 200;

export function TypeaheadMultiSelect<T extends TypeaheadItem>({
  placeholder,
  fetcher,
  selectedIds,
  selectedItems,
  onChange,
  renderItem,
  renderSelectedLabel,
  pillTone = 'neutral',
  emptyState,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
}: TypeaheadMultiSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<readonly T[]>([]);
  const [loading, setLoading] = useState(false);

  // Tracks the most recent fetch so a slower, stale response can't clobber
  // a fresher one (e.g. user typed "an" then "ann" — the "an" response
  // returning second would otherwise overwrite the "ann" results).
  const fetchSeqRef = useRef(0);
  const debounceTimerRef = useRef<number | null>(null);

  const itemById = useMemo(() => {
    const m = new Map<string, T>();
    for (const it of selectedItems) m.set(it.id, it);
    for (const it of results) m.set(it.id, it);
    return m;
  }, [selectedItems, results]);

  const runFetch = useCallback(
    (q: string) => {
      const seq = ++fetchSeqRef.current;
      setLoading(true);
      fetcher(q)
        .then((items) => {
          if (fetchSeqRef.current !== seq) return;
          setResults(items);
        })
        .catch(() => {
          if (fetchSeqRef.current !== seq) return;
          setResults([]);
        })
        .finally(() => {
          if (fetchSeqRef.current !== seq) return;
          setLoading(false);
        });
    },
    [fetcher],
  );

  useEffect(() => {
    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      runFetch(query);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [query, runFetch]);

  const options: ReadonlyArray<ComboboxOption<string>> = useMemo(
    () =>
      results.map((it) => ({
        value: it.id,
        label: it.id, // placeholder; renderOption draws the real label
      })),
    [results],
  );

  const selectedOptions: ReadonlyArray<ComboboxOption<string>> = useMemo(
    () =>
      selectedItems.map((it) => ({
        value: it.id,
        label: it.id, // placeholder; renderChip draws the real label
      })),
    [selectedItems],
  );

  return (
    <MultiCombobox<string>
      {...(id !== undefined ? { id } : {})}
      {...(ariaDescribedBy !== undefined ? { 'aria-describedby': ariaDescribedBy } : {})}
      {...(ariaInvalid !== undefined ? { 'aria-invalid': ariaInvalid } : {})}
      {...(ariaRequired !== undefined ? { 'aria-required': ariaRequired } : {})}
      placeholder={placeholder ?? 'Search…'}
      options={options}
      value={selectedIds as readonly string[]}
      selectedItems={selectedOptions}
      onChange={(nextIds) => {
        const nextItems: T[] = [];
        for (const nextId of nextIds) {
          const found = itemById.get(nextId);
          if (found !== undefined) nextItems.push(found);
        }
        onChange(nextIds, nextItems);
      }}
      onQueryChange={setQuery}
      noInternalFilter
      loading={loading}
      {...(emptyState !== undefined ? { emptyLabel: emptyState } : {})}
      chipTone={pillTone}
      renderOption={(opt) => {
        const item = itemById.get(opt.value);
        return item !== undefined ? renderItem(item) : null;
      }}
      renderChip={(opt, { onRemove }) => {
        const item = itemById.get(opt.value);
        return (
          <Pill
            size="md"
            tone={pillTone}
            onRemove={onRemove}
            removeAriaLabel="Remove"
          >
            {item !== undefined ? renderSelectedLabel(item) : opt.label}
          </Pill>
        );
      }}
    />
  );
}
